import React from 'react';
import SiteShell from './SiteShell';

/* 1 — Hero */
const Hero = () => (
  <section className="site-hero">
    <div className="site-wrap">
      <div className="site-eyebrow" style={{ color: '#6bd69b' }}>
        Tarpaulin supply chain, end to end
      </div>
      <h1>Buy and sell tarpaulin without the phone calls</h1>
      <p>
        Tarpex.live connects manufacturers, distributors and dealers on one platform — live
        catalogues, GST-correct invoices, courier-rated delivery and payments that settle
        themselves.
      </p>

      <div className="site-hero-actions">
        <a className="site-btn site-btn-primary" href="#sellers">Start selling</a>
        <a className="site-btn site-btn-light" href="#buyers">Browse as a buyer</a>
      </div>

      <div className="site-hero-stats">
        <div><b>GST-ready</b><span>CGST/SGST and IGST handled automatically</span></div>
        <div><b>Pan-India</b><span>Courier rates quoted at checkout</span></div>
        <div><b>Verified</b><span>Every seller checked against their GSTIN</span></div>
      </div>
    </div>
  </section>
);

/* 2 — What the platform does */
const FEATURES = [
  {
    icon: '▦',
    title: 'A live catalogue',
    body: 'HDPE, PVC, canvas, covers and liners with real GSM, sizes, colours and stock — not a PDF price list from last season.',
  },
  {
    icon: '₹',
    title: 'Prices that hold',
    body: 'Bulk slabs apply automatically as quantities rise, and every quote shows the delivered cost including GST.',
  },
  {
    icon: '🚚',
    title: 'Delivery, rated upfront',
    body: 'Charges are calculated from real weight and both pincodes through the courier, so the total at checkout is the total.',
  },
  {
    icon: '⛨',
    title: 'Verified counterparties',
    body: 'GSTINs are checked for validity and active status before a business can trade. No active GSTIN, no seller account.',
  },
  {
    icon: '⛁',
    title: 'Stock that is honest',
    body: 'Inventory is reserved when an order is confirmed and issued when it ships, with a movement ledger behind every number.',
  },
  {
    icon: '📄',
    title: 'Paperwork done for you',
    body: 'Tax invoices, e-way-ready consignment details and settlement statements generate themselves.',
  },
];

const Features = () => (
  <section className="site-section" id="platform">
    <div className="site-wrap">
      <div className="site-eyebrow">The platform</div>
      <h2 className="site-h2">Everything the trade runs on, in one place</h2>
      <p className="site-lead">
        Tarpaulin is sold on specification, quantity and delivered price. Tarpex handles all
        three, so both sides can agree in minutes instead of over three days of calls.
      </p>

      <div className="site-grid site-grid-3">
        {FEATURES.map(f => (
          <div className="site-card" key={f.title}>
            <div className="site-icon">{f.icon}</div>
            <h3>{f.title}</h3>
            <p>{f.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* 3 — How it works */
const STEPS = [
  { n: 1, title: 'Sign in with your mobile', body: 'One OTP. No forms, no passwords to lose.' },
  { n: 2, title: 'Add your GSTIN', body: 'We check it is valid and active. Active GSTINs are approved on the spot — no waiting on a review queue.' },
  { n: 3, title: 'Buy or sell', body: 'Buyers browse and order; sellers list stock and receive orders. The same account can switch between both.' },
  { n: 4, title: 'Ship and settle', body: 'Book the courier from the order, track it to the door, and see your settlement once it is delivered.' },
];

const HowItWorks = () => (
  <section className="site-section alt" id="how">
    <div className="site-wrap">
      <div className="site-eyebrow">How it works</div>
      <h2 className="site-h2">Trading in four steps</h2>
      <p className="site-lead">From first sign-in to money in the bank, without leaving the app.</p>

      <div className="site-grid site-grid-2">
        {STEPS.map(s => (
          <div className="site-card site-step" key={s.n}>
            <div className="site-step-n">{s.n}</div>
            <div>
              <h3 style={{ marginTop: 0 }}>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* 5 — For sellers */
const SELLER_POINTS = [
  'List products with photos taken on your phone, tags, GSM, colours and weight',
  'Set your own MOQ, bulk slabs and return policy per product',
  'See your real margin after commission before you publish a price',
  'Stock, low-stock alerts and demand in one screen',
  'Orders, packing, dispatch and delivery tracked through one status flow',
  'Settlement statements for every delivered order',
];

const ForSellers = () => (
  <section className="site-section alt" id="sellers">
    <div className="site-wrap">
      <div className="site-grid site-grid-2" style={{ alignItems: 'center', gap: 48 }}>
        <div>
          <div className="site-eyebrow">For manufacturers &amp; distributors</div>
          <h2 className="site-h2">Sell to the whole country from your phone</h2>
          <p className="site-lead" style={{ marginBottom: 26 }}>
            Put your catalogue in front of verified buyers, and let the platform handle pricing
            maths, delivery rates and paperwork.
          </p>

          <ul className="site-list">
            {SELLER_POINTS.map(point => (
              <li key={point}>
                <span className="tick">✓</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="site-card" style={{ background: 'var(--s-bg)' }}>
          <div className="site-eyebrow">What a seller sees</div>
          <h3 style={{ fontSize: 21, marginTop: 6 }}>Your catalogue at a glance</h3>
          <div className="site-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 18 }}>
            {[
              ['Total demand', 'units ordered'],
              ['Best seller', 'what is moving'],
              ['Low stock', 'restock before it bites'],
              ['Out of stock', 'fix it in two taps'],
            ].map(([label, note]) => (
              <div key={label} style={{ background: '#fff', border: '1px solid var(--s-line)', borderRadius: 12, padding: 14 }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{label}</div>
                <div style={{ color: 'var(--s-muted)', fontSize: 13 }}>{note}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* 6 — For buyers */
const BUYER_POINTS = [
  'Search by name, brand, colour, GSM, seller or price',
  'See the delivered price — goods, GST and courier — before you commit',
  'Buy without a GSTIN up to the platform limit, add one to go beyond it',
  'Track every order from confirmed to delivered',
  'Return or exchange against the policy shown before you bought',
  'Pay by UPI, card or netbanking, or settle on your credit terms',
];

const ForBuyers = () => (
  <section className="site-section" id="buyers">
    <div className="site-wrap">
      <div className="site-eyebrow">For dealers &amp; retailers</div>
      <h2 className="site-h2">Know the landed cost before you order</h2>
      <p className="site-lead">
        No more quotes that leave out freight and tax. What you see at checkout is what you pay.
      </p>

      <div className="site-grid site-grid-3">
        {BUYER_POINTS.map(point => (
          <div className="site-card" key={point}>
            <div className="site-icon">✓</div>
            <p style={{ fontSize: 15.5, color: 'var(--s-ink)', fontWeight: 500 }}>{point}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* 4 — What the platform trades */
const CATEGORIES = [
  { name: 'HDPE tarpaulin', body: 'Laminated woven polyethylene with UV stabilisers — the everyday cover for godowns, trucks and construction sites.', gsm: '90–300 GSM' },
  { name: 'PVC tarpaulin', body: 'Coated heavy-duty fabric for long service life and hard wear, where an HDPE sheet would not last the season.', gsm: '400–900 GSM' },
  { name: 'Canvas tarpaulin', body: 'Cotton duck canvas, breathable and traditional, still specified for machinery and grain.', gsm: '250–600 GSM' },
  { name: 'Truck & trailer covers', body: 'Ready-made covers cut to standard body sizes, with reinforced eyelets and hemmed edges.', gsm: 'Made to size' },
  { name: 'Agricultural covers', body: 'Crop, silage and grain covers, and shade material for nurseries and polyhouses.', gsm: '75–200 GSM' },
  { name: 'Pond & water liners', body: 'Waterproof geomembrane lining for farm ponds, tanks and effluent containment.', gsm: '300–750 GSM' },
];

const Categories = () => (
  <section className="site-section" id="categories">
    <div className="site-wrap">
      <div className="site-eyebrow">What is traded here</div>
      <h2 className="site-h2">Every grade the trade actually asks for</h2>
      <p className="site-lead">
        Tarpaulin is bought on specification, not on a picture. Each listing carries its material,
        GSM, size, colour and weight, so a buyer can compare like with like before they order.
      </p>

      <div className="site-grid site-grid-3">
        {CATEGORIES.map(c => (
          <div className="site-card" key={c.name}>
            <div className="site-cat-gsm">{c.gsm}</div>
            <h3>{c.name}</h3>
            <p>{c.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* 7 — About us */
const ABOUT_STATS = [
  ['One platform', 'Catalogue, orders, delivery, payments and paperwork in one place'],
  ['Built in India', 'For Indian GST, Indian couriers and Indian trading terms'],
  ['Verified only', 'Every selling business is checked against its GSTIN'],
];

const About = () => (
  <section className="site-section alt" id="about">
    <div className="site-wrap">
      <div className="site-grid site-grid-2" style={{ alignItems: 'start', gap: 48 }}>
        <div>
          <div className="site-eyebrow">About us</div>
          <h2 className="site-h2">We are putting a paper trade online</h2>

          <p style={{ color: 'var(--s-muted)', fontSize: 16.5, marginBottom: 18 }}>
            Tarpaulin moves through India on phone calls, WhatsApp photographs and handwritten
            rate lists. A dealer rings four suppliers for a price, waits a day for each, and still
            does not know the landed cost until the goods arrive with a freight bill attached.
          </p>
          <p style={{ color: 'var(--s-muted)', fontSize: 16.5, marginBottom: 18 }}>
            Tarpex.live exists to close that gap. Sellers publish real specifications and real
            stock. Buyers see the delivered price — goods, GST and courier — before they commit.
            Orders, invoices, dispatch and settlement all follow from the same record, so nobody
            is reconciling three versions of the truth at the end of the month.
          </p>
          <p style={{ color: 'var(--s-muted)', fontSize: 16.5 }}>
            We are built and operated by <b>Stackerio</b>, and we make money only when a trade
            completes — which keeps our interests pointed the same way as yours.
          </p>
        </div>

        <div className="site-grid" style={{ gap: 16 }}>
          {ABOUT_STATS.map(([title, body]) => (
            <div className="site-card" key={title} style={{ background: 'var(--s-bg)' }}>
              <h3 style={{ margin: '0 0 6px' }}>{title}</h3>
              <p>{body}</p>
            </div>
          ))}

          <div className="site-card" style={{ background: 'var(--s-bg)' }}>
            <h3 style={{ margin: '0 0 6px' }}>Talk to us</h3>
            <p style={{ marginBottom: 14 }}>
              Questions about listing, delivery or settlement? We answer within a working day.
            </p>
            <a className="site-btn site-btn-primary" href="mailto:support@tarpex.live">
              support@tarpex.live
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* 8 — FAQ + closing call to action */
const FAQS = [
  ['Do I need a GST number to buy?', 'No. You can buy up to the platform limit without one. Add a valid, active GSTIN in the app to trade beyond it and to claim input credit.'],
  ['How fast is a seller account approved?', 'If your GSTIN is valid and active, immediately — there is no manual queue. Your first product listing is reviewed before it goes live.'],
  ['Who arranges delivery?', 'The platform does. Charges are quoted from the real weight of your order and the distance between the pickup and delivery pincodes, and shown separately at checkout.'],
  ['How do returns work?', 'Every product shows its return policy before you buy — full return, replacement only, or no return. Requests go to the seller, and the platform can step in.'],
  ['How do I delete my account?', 'From the Delete your account page, or from the app. We verify it is you by SMS and remove your personal details; trading records are retained where the law requires.'],
];

const Faq = () => (
  <section className="site-section" id="faq">
    <div className="site-wrap">
      <div className="site-eyebrow">Questions</div>
      <h2 className="site-h2">Before you sign up</h2>

      <div className="site-faq" style={{ marginBottom: 56 }}>
        {FAQS.map(([q, a]) => (
          <details key={q}>
            <summary>{q}</summary>
            <p>{a}</p>
          </details>
        ))}
      </div>

      <div className="site-cta">
        <h2>Put your tarpaulin business on Tarpex</h2>
        <p>
          Sign in with your mobile number and you are trading the same day. Buying is free,
          and selling starts free too.
        </p>
        {/* A public page's main action should be for buyers and sellers, not
            for the platform team — the console is reachable from the footer. */}
        <div className="site-hero-actions">
          <a className="site-btn site-btn-light" href="mailto:support@tarpex.live">
            Talk to us
          </a>
          <a className="site-btn site-btn-primary" href="#how">
            See how it works
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Landing = () => (
  <SiteShell>
    <Hero />
    <Features />
    <HowItWorks />
    <Categories />
    <ForSellers />
    <ForBuyers />
    <About />
    <Faq />
  </SiteShell>
);

export default Landing;
